import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { getEvent } from '@/lib/api/core';

/**
 * useEventCatalog — metadata del evento vía el proxy de api-core
 * (`GET /v1/events/:id`).
 *
 * Sólo carga metadata del evento; las localidades / inventario se cargan por
 * separado vía `useInventory()` (`GET /v1/events/:id/inventory` de taquilla)
 * y degradan de forma digna si están vacías. Así "ver el evento" queda
 * desacoplado de "comprar": el detalle nunca se rompe por falta de
 * inventario.
 */

export type EventCatalogEvent = {
  id: string;
  title: string;
  description: string | null;
  date: string | null;
  venue: string | null;
  /** Nombre de la ciudad (enriquecido por api-core: `city_name`). */
  cityName: string | null;
  /** Nombre de la categoría (enriquecido por api-core: `category_name`). */
  categoryName: string | null;
  status: string | null;
  coverImage: string | null;
  brandId: string | null;
  /** ISO-4217 del evento (Ola 3 multimoneda). */
  currency: string | null;
  /** { en: { title, description } } — localiza contenido por idioma. */
  translations: Record<string, { title?: string; description?: string }> | null;
};

export type EventCatalogLocality = {
  id: string;
  stageId: string;
  name: string;
  priceFullCents: number | null;
  pricePromoCents: number | null;
};

export type EventCatalog = {
  event: EventCatalogEvent;
  /**
   * Conservado por compatibilidad de tipo (mock fixtures). En el flujo
   * real las localidades vienen de `useInventory()`, no de acá.
   */
  localities: EventCatalogLocality[];
};

async function fetchEventCatalog(eventId: string): Promise<EventCatalog> {
  const e = await getEvent(eventId);
  const venue = typeof e.venue === 'string' ? e.venue.trim() : '';
  const cityName = typeof e.city_name === 'string' ? e.city_name.trim() : '';
  const categoryName = typeof e.category_name === 'string' ? e.category_name.trim() : '';
  return {
    event: {
      id: String(e.id),
      title: typeof e.title === 'string' && e.title.trim() ? e.title : 'Evento',
      description: (e.description as string | null) ?? null,
      date: (e.date as string | null) ?? null,
      venue: venue || null,
      cityName: cityName || null,
      categoryName: categoryName || null,
      status: (e.status as string | null) ?? null,
      coverImage: (e.cover_image as string | null) ?? null,
      brandId: (e.brand_id as string | null) ?? null,
      currency: typeof e.currency === 'string' ? e.currency : null,
      translations: (e.translations as EventCatalogEvent['translations']) ?? null,
    },
    localities: [],
  };
}

// Mock-mode hook (ROUND6 P2): when VITE_USE_MOCK_DATA=true the hook
// returns deterministic event fixtures so the /event/:id page renders
// without depending on public.events RLS exposure.
// eslint-disable-next-line @typescript-eslint/no-var-requires
import { isMockMode, getMockEventCatalog } from '@/lib/mock-data';

export function useEventCatalog(eventId: string | undefined): UseQueryResult<EventCatalog> {
  return useQuery({
    queryKey: ['event-catalog', eventId, isMockMode() ? 'mock' : 'live'],
    queryFn: async () => {
      if (!eventId) throw new Error('useEventCatalog: eventId is required');
      if (isMockMode()) {
        await new Promise((r) => setTimeout(r, 120));
        const mock = getMockEventCatalog(eventId);
        if (!mock) {
          throw Object.assign(new Error('Mock event not found'), {
            status: 404,
            code: 'EVENT_NOT_FOUND',
          });
        }
        return mock;
      }
      return fetchEventCatalog(eventId);
    },
    enabled: !!eventId,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}
